import { useState } from 'react';
import useFetchDataRoomFiles from '../../../DataRoom/useFetchDataRoomFiles';
import { generateGstAnalysisReport } from '../../../../store/dashboard/revenueVendorExpenses/revenueVendorExpensesApiHelpers';
import { GstAnalysisReportDoc, GstRangeParams } from '../../../../store/dashboard/revenueVendorExpenses/types';

// Backend generates a multi-sheet .xlsx (GstService.generateAndUploadGstAnalysisReport);
// the extension drives viewer MIME resolution, so the fallback must stay .xlsx.
const GST_ANALYSIS_REPORT_FALLBACK_NAME = 'GST Analysis Report.xlsx';

/**
 * Generates the GST Analysis report and opens it in the shared dataroom viewer.
 * Reuses useFetchDataRoomFiles for the presigned-URL fetch and download, and
 * keeps the generate → open → download orchestration out of the component.
 */
export const useGstAnalysisReport = () => {
    const [generatingReport, setGeneratingReport] = useState(false);
    const [reportDoc, setReportDoc] = useState<GstAnalysisReportDoc | null>(null);
    const { handleFileClick, showFile, setShowFile, fileUrl, fileMimeType, downloadTheFile, downloadingFile } = useFetchDataRoomFiles();

    // rangeParams: the GST tab's applied "Time Period" range, so the workbook covers exactly the
    // window on screen. Absent means the backend keeps deriving its own range (today's behaviour).
    const openReport = async (orgId?: string, rangeParams?: GstRangeParams) => {
        if (!orgId || generatingReport) return;
        setGeneratingReport(true);
        try {
            const report = await generateGstAnalysisReport(orgId, rangeParams);
            if (report?.mnemosyneDocId) {
                const doc: GstAnalysisReportDoc = {
                    docId: report.mnemosyneDocId,
                    name: report.fileName ?? GST_ANALYSIS_REPORT_FALLBACK_NAME,
                };
                setReportDoc(doc);
                await handleFileClick(doc.docId, doc.name);
            }
        } finally {
            // Always clear the spinner, even if handleFileClick rejects.
            setGeneratingReport(false);
        }
    };

    const closeReport = () => setShowFile(false);

    const downloadReport = () => {
        if (reportDoc?.docId) downloadTheFile(reportDoc.docId, reportDoc.name);
    };

    return { generatingReport, reportDoc, showFile, fileUrl, fileMimeType, downloadingFile, openReport, closeReport, downloadReport };
};
